import { css } from 'aphrodite';
import * as React from 'react';
import {
	IMetaMetricsSyncError,
	IMetaVisibleAsset,
	META_AD_ACCOUNT_STATUS_LABELS,
	useMetaAssignManagedMutation,
	useMetaConnectionQuery,
	useMetaUnassignManagedMutation,
	useMetaVisibleAssetsQuery,
} from '../../../../queries/Admin/Meta';
import { Button } from '../../../../web/components-v2/Button';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Pill } from '../../../../web/components-v2/Pill';
import { formatUtcTimestamp, getErrorMessage, tonePillColor } from '../utils';
import { styleSheet } from './styles';

// Stable empty fallback so memo deps don't bust every render while the connection
// query is loading or errored.
const EMPTY_IDS: string[] = [];

interface IProps {
	levitateAccountId: string;
	levitateAccountName?: string | null;
	onClose: () => void;
}

export const MetaConnectionModal: React.FC<IProps> = ({ levitateAccountId, levitateAccountName, onClose }) => {
	const connection = useMetaConnectionQuery({ levitateAccountId });
	const visibleAssets = useMetaVisibleAssetsQuery();
	const unassignMutation = useMetaUnassignManagedMutation();
	const assignMutation = useMetaAssignManagedMutation();

	const [pickerOpen, setPickerOpen] = React.useState(false);
	const [pickerSearch, setPickerSearch] = React.useState('');
	// Track the specific id being removed so each row can show its own in-flight
	// state — previously every Remove button disabled together because they all
	// read `unassignMutation.isPending`.
	const [pendingRemoveId, setPendingRemoveId] = React.useState<string | null>(null);

	// Either mutation in flight blocks the other affordance. POST replaces the
	// full managed list, so an Add fired during an in-flight Remove would replay
	// the pre-delete snapshot and resurrect the row being removed; symmetrically
	// a Remove during an in-flight Add could be clobbered when the POST returns.
	const mutationInFlight = assignMutation.isPending || unassignMutation.isPending;

	const assetsByMetaId = React.useMemo(() => {
		const map = new Map<string, IMetaVisibleAsset>();
		(visibleAssets.data ?? []).forEach(a => map.set(a.metaAdAccountId, a));
		return map;
	}, [visibleAssets.data]);

	const closePicker = React.useCallback(() => {
		setPickerOpen(false);
		setPickerSearch('');
	}, []);

	// The mutation/GET response carries the managed list directly; whether the ads
	// team is "managing" is just whether that list is non-empty. Use the stable
	// upstream array (or a stable EMPTY const) so downstream memos don't bust.
	const managedAdAccountIds = connection.data?.managedAdAccountIds ?? EMPTY_IDS;
	const isAdsTeamManaging = managedAdAccountIds.length > 0;
	const hasCustomerToken = !!connection.data?.hasCustomerToken;

	const handleRemove = (metaAdAccountId: string) => {
		setPendingRemoveId(metaAdAccountId);
		unassignMutation.mutate({ levitateAccountId, metaAdAccountId }, { onSettled: () => setPendingRemoveId(null) });
	};

	const handleAddManaged = (metaAdAccountId: string) => {
		// Close the picker before firing the mutation so a fast second click can't
		// fire another POST against the same stale `managedAdAccountIds` snapshot
		// (POST replaces the full list — racing two adds would clobber the first).
		closePicker();
		const nextManaged = Array.from(new Set([...managedAdAccountIds, metaAdAccountId]));
		assignMutation.mutate({ levitateAccountId, metaAdAccountIds: nextManaged });
	};

	// Customer-OAuth-selected ad accounts. The GET endpoint is authoritative now
	// that the backend ships the full `ManagedConnectionResponse`. The 404-
	// synthesize fallback in `useMetaConnectionQuery` returns no `adAccountIds`,
	// so render an empty list in that case (no prior connection ⇒ no OAuth-
	// selected accounts to show).
	const customerSelectedIds = connection.data?.adAccountIds ?? EMPTY_IDS;

	// Build the managed Set once per managed-list change, then reuse it inside the
	// filter — earlier this was `new Set(...)` inside the filter callback, allocating
	// a Set per visible asset.
	const managedSet = React.useMemo(() => new Set(managedAdAccountIds), [managedAdAccountIds]);
	const availableToAdd = React.useMemo(
		() => (visibleAssets.data ?? []).filter(a => !managedSet.has(a.metaAdAccountId)),
		[visibleAssets.data, managedSet]
	);
	const filteredAvailable = React.useMemo(() => {
		const q = pickerSearch.trim().toLowerCase();
		if (!q) return availableToAdd;
		return availableToAdd.filter(a => a.name.toLowerCase().includes(q) || a.metaAdAccountId.toLowerCase().includes(q));
	}, [availableToAdd, pickerSearch]);

	// Policy: the ads team manages a single Meta ad account per Levitate account.
	// Once a managed entry exists, hide the +Add affordance entirely; legacy data
	// with 2+ managed rows still renders via the AdAccountList so operators can
	// Remove down to the cap.
	const canAddManaged = managedAdAccountIds.length === 0;

	return (
		<Dialog open={true} onOpenChange={open => !open && onClose()}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Meta connection — {levitateAccountName || `Account ${levitateAccountId}`}</DialogTitle>
					<div className={css(styleSheet.subtitle)}>account id: {levitateAccountId}</div>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.body)}>
						{connection.isPending ? (
							<ModalSkeleton />
						) : connection.isError ? (
							<div className={css(styleSheet.errorBanner)}>
								{getErrorMessage(connection.error) || 'Failed to load connection.'}
							</div>
						) : (
							<>
								<section className={css(styleSheet.section)}>
									<h3 className={css(styleSheet.sectionTitle)}>Status</h3>
									<div className={css(styleSheet.statusLines)}>
										<div className={css(styleSheet.statusLine)}>
											<span className={css(hasCustomerToken ? styleSheet.statusDotOn : styleSheet.statusDotOff)} />
											<span>{hasCustomerToken ? 'Customer connected via OAuth' : 'No customer OAuth'}</span>
										</div>
										<div className={css(styleSheet.statusLine)}>
											<span className={css(isAdsTeamManaging ? styleSheet.statusDotOn : styleSheet.statusDotOff)} />
											<span>
												{isAdsTeamManaging ? 'Levitate ads team is also managing' : 'Levitate ads team is not managing'}
											</span>
										</div>
									</div>
								</section>

								<LeadIngestionSection
									lastPollTimestamp={connection.data?.lastPollTimestamp}
									lastPollLeadCount={connection.data?.lastPollLeadCount}
									lastPollError={connection.data?.lastPollError}
									lastLeadReceivedAt={connection.data?.lastLeadReceivedAt}
									lastTokenRefreshedAt={connection.data?.lastTokenRefreshedAt}
								/>

								<MetricsSyncSection
									lastMetricsSyncAt={connection.data?.lastMetricsSyncAt}
									lastMetricsSyncError={connection.data?.lastMetricsSyncError}
								/>

								<section className={css(styleSheet.section)}>
									<h3 className={css(styleSheet.sectionTitle)}>Customer-selected ad accounts</h3>
									<p className={css(styleSheet.sectionDescription)}>
										Accounts the customer picked in their own Meta configure flow. They drive lead polling. Read-only
										here — the customer is the source of truth.
									</p>
									<AdAccountList
										ids={customerSelectedIds}
										assetsByMetaId={assetsByMetaId}
										assetsLoading={visibleAssets.isPending || visibleAssets.isFetching}
									/>
								</section>

								<section className={css(styleSheet.section)}>
									<h3 className={css(styleSheet.sectionTitle)}>Ads-team managed ad account</h3>
									<p className={css(styleSheet.sectionDescription)}>
										The Levitate ads team operates one ad account on the customer's behalf via the System User token.
									</p>
									<AdAccountList
										ids={managedAdAccountIds}
										assetsByMetaId={assetsByMetaId}
										onRemove={handleRemove}
										pendingRemoveId={pendingRemoveId}
										removeDisabled={assignMutation.isPending}
										// Used to disambiguate "(no longer accessible)" from "just-assigned, visibleAssets
										// refetch hasn't landed yet" — the latter renders as a quiet placeholder instead.
										assetsLoading={visibleAssets.isPending || visibleAssets.isFetching}
									/>
									{/*
									  Mutation failures surface as toasts via the explicit `onError` handlers in
									  the assign/unassign mutation hooks — no inline banner needed here.
									*/}
									{canAddManaged ? (
										pickerOpen ? (
											<div className={css(styleSheet.pickerWrapper)}>
												{availableToAdd.length === 0 ? (
													<div className={css(styleSheet.emptyHint)}>No additional visible ad accounts to add.</div>
												) : (
													<>
														<Input
															type='search'
															placeholder='Search by name or id'
															value={pickerSearch}
															onChange={e => setPickerSearch(e.target.value)}
															autoFocus={true}
														/>
														<div className={css(styleSheet.pickerScroll)}>
															{filteredAvailable.length === 0 ? (
																<div className={css(styleSheet.emptyHint)}>No ad accounts match this search.</div>
															) : (
																filteredAvailable.map(asset => (
																	<Button
																		variant='link'
																		key={asset.metaAdAccountId}
																		onClick={() => handleAddManaged(asset.metaAdAccountId)}
																		disabled={mutationInFlight}
																		styles={[styleSheet.pickerRow]}
																	>
																		<span className={css(styleSheet.adAccountId)}>{asset.metaAdAccountId}</span>
																		<span className={css(styleSheet.adAccountName)}>{asset.name}</span>
																	</Button>
																))
															)}
														</div>
													</>
												)}
												<div className={css(styleSheet.pickerFooter)}>
													{availableToAdd.length > 0 ? (
														<span className={css(styleSheet.pickerCount)}>
															{filteredAvailable.length} of {availableToAdd.length} shown
														</span>
													) : null}
													<Button
														variant='secondary'
														size='sm'
														onClick={closePicker}
														styles={[styleSheet.pickerCloseButton]}
													>
														Close picker
													</Button>
												</div>
											</div>
										) : (
											<Button variant='secondary' onClick={() => setPickerOpen(true)} disabled={mutationInFlight}>
												+ Add managed ad account
											</Button>
										)
									) : null}
								</section>
							</>
						)}
					</div>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};

const AdAccountList: React.FC<{
	ids: string[];
	assetsByMetaId: Map<string, IMetaVisibleAsset>;
	onRemove?: (metaAdAccountId: string) => void;
	pendingRemoveId?: string | null;
	// Block Remove while an assign is in flight — a DELETE that races with the
	// pending POST would be silently undone by the POST's full-list replacement.
	removeDisabled?: boolean;
	// True when the visibleAssets lookup table is still loading or refetching. Used
	// to distinguish "we don't know this id yet" (placeholder) from "this id is
	// genuinely no longer accessible to the BM" (final-state label).
	assetsLoading?: boolean;
}> = ({ ids, assetsByMetaId, onRemove, pendingRemoveId, removeDisabled, assetsLoading }) => {
	if (ids.length === 0) {
		return <div className={css(styleSheet.emptyHint)}>None.</div>;
	}
	return (
		<div className={css(styleSheet.adAccountList)}>
			{ids.map(id => {
				const asset = assetsByMetaId.get(id);
				const status = asset
					? (META_AD_ACCOUNT_STATUS_LABELS[asset.accountStatus] ?? {
							label: `Status ${asset.accountStatus}`,
							tone: 'neutral' as const,
						})
					: null;
				// `(no longer accessible)` is only definitive once visibleAssets has settled.
				// While it's still fetching, a just-assigned id would otherwise flash that
				// alarming label until the refetch lands.
				const missingLabel = assetsLoading ? '…' : '(no longer accessible)';
				return (
					<div key={id} className={css(styleSheet.adAccountRow)}>
						<div className={css(styleSheet.adAccountLeft)}>
							<span className={css(styleSheet.adAccountId)}>{id}</span>
							<span className={css(styleSheet.adAccountName)}>{asset ? asset.name : missingLabel}</span>
						</div>
						{status ? <Pill variant='label' color={tonePillColor(status.tone)} label={status.label} /> : null}
						{onRemove ? (
							<Button
								variant='destructive-secondary'
								size='sm'
								onClick={() => onRemove(id)}
								disabled={pendingRemoveId !== null || !!removeDisabled}
								isLoading={pendingRemoveId === id}
								styles={[styleSheet.removeButton]}
							>
								Remove
							</Button>
						) : null}
					</div>
				);
			})}
		</div>
	);
};

// Surfaces the daily metrics-sweep observability metadata so support / the ads
// team can spot stuck connections. Hidden when neither field is populated
// (e.g. on a freshly-synthesized 404-fallback connection).
const MetricsSyncSection: React.FC<{
	lastMetricsSyncAt?: string;
	lastMetricsSyncError?: IMetaMetricsSyncError;
}> = ({ lastMetricsSyncAt, lastMetricsSyncError }) => {
	if (!lastMetricsSyncAt && !lastMetricsSyncError) return null;
	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Metrics sync</h3>
			<p className={css(styleSheet.sectionDescription)}>
				Status of the daily Hangfire sweep that refreshes this customer's snapshot store.
			</p>
			{lastMetricsSyncAt ? (
				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.detailLabel)}>Last sync:</span>
					<span>{formatUtcTimestamp(lastMetricsSyncAt)}</span>
				</div>
			) : null}
			{lastMetricsSyncError ? (
				<div className={css(styleSheet.detailErrorBox)}>
					<div className={css(styleSheet.detailRow)}>
						<span className={css(styleSheet.detailLabel)}>Last error:</span>
						<span>{formatUtcTimestamp(lastMetricsSyncError.timestamp)}</span>
					</div>
					{lastMetricsSyncError.metaAdAccountId ? (
						<div className={css(styleSheet.detailRow)}>
							<span className={css(styleSheet.detailLabel)}>Ad account:</span>
							<span className={css(styleSheet.detailId)}>{lastMetricsSyncError.metaAdAccountId}</span>
						</div>
					) : (
						<div className={css(styleSheet.detailRow)}>
							<span className={css(styleSheet.detailLabel)}>Scope:</span>
							<span>Connection-level (e.g. token unavailable)</span>
						</div>
					)}
					<pre className={css(styleSheet.detailErrorMessage)}>{lastMetricsSyncError.message}</pre>
				</div>
			) : null}
		</section>
	);
};

// Surfaces the customer's lead-polling health: most-recent poll attempt and lead
// count, most-recent error (if any), last lead actually received, and last
// successful customer-token refresh. The admin surface reports these regardless
// of customer-OAuth state — managed-only connections still poll via the System
// User token, and their health must be visible.
const LeadIngestionSection: React.FC<{
	lastPollTimestamp?: string;
	lastPollLeadCount?: number;
	lastPollError?: string;
	lastLeadReceivedAt?: string;
	lastTokenRefreshedAt?: string;
}> = ({ lastPollTimestamp, lastPollLeadCount, lastPollError, lastLeadReceivedAt, lastTokenRefreshedAt }) => {
	const anyPresent = !!(lastPollTimestamp || lastPollError || lastLeadReceivedAt || lastTokenRefreshedAt);
	if (!anyPresent) return null;
	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Lead ingestion</h3>
			<p className={css(styleSheet.sectionDescription)}>
				Health of the customer's Meta lead-form polling and token-refresh loop.
			</p>
			{lastPollTimestamp ? (
				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.detailLabel)}>Last poll:</span>
					<span>
						{formatUtcTimestamp(lastPollTimestamp)}
						{typeof lastPollLeadCount === 'number'
							? ` · ${lastPollLeadCount} ${lastPollLeadCount === 1 ? 'lead' : 'leads'}`
							: null}
					</span>
				</div>
			) : null}
			{lastLeadReceivedAt ? (
				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.detailLabel)}>Last lead received:</span>
					<span>{formatUtcTimestamp(lastLeadReceivedAt)}</span>
				</div>
			) : null}
			{lastTokenRefreshedAt ? (
				<div className={css(styleSheet.detailRow)}>
					<span className={css(styleSheet.detailLabel)}>Token refreshed:</span>
					<span>{formatUtcTimestamp(lastTokenRefreshedAt)}</span>
				</div>
			) : null}
			{lastPollError ? (
				<div className={css(styleSheet.detailErrorBox)}>
					<div className={css(styleSheet.detailRow)}>
						<span className={css(styleSheet.detailLabel)}>Last poll error:</span>
					</div>
					<pre className={css(styleSheet.detailErrorMessage)}>{lastPollError}</pre>
				</div>
			) : null}
		</section>
	);
};

const ModalSkeleton: React.FC = () => (
	<div aria-busy='true' className={css(styleSheet.skeletonStack)}>
		<div className={css(styleSheet.skeletonBar, styleSheet.skeletonBarSm)} />
		<div className={css(styleSheet.skeletonBar, styleSheet.skeletonBarLg)} />
		<div className={css(styleSheet.skeletonBar, styleSheet.skeletonBarMd)} />
	</div>
);
